<template>
  <main-layout :title="$t('Dashboard')" :hide-user-info-tasks-card="true">
    <template v-slot:body>
      <h2
        v-if="user && user.first_name"
        class="text-h5 text-sm-h4 mt-6 mb-6 font-weight-medium"
      >
        👋 {{ $t('Welcome') }}, {{ user.first_name }}
      </h2>

      <div
        v-if="pendingOwnerInquiries && pendingOwnerInquiries.length"
        class="mt-12"
      >
        <div class="d-flex align-center justify-space-between mb-3">
          <v-badge
            :content="pendingOwnerInquiries.length"
            inline
            color="primary"
          >
            <h2 class="text-h5 mb-0 font-weight-bold">{{ $t('dashboard.new_owner_inquiries') }}</h2>
          </v-badge>
          <router-link
            :to="{ name: 'inquiries' }"
            class="font-weight-medium"
          >
            {{ $t('View all') }}
          </router-link>
        </div>
        <v-card outlined class="pa-3 rounded-lg">
          <inquries-in-table :inquries="pendingOwnerInquiries" />
        </v-card>
      </div>

      <div
        v-if="hasBusinesses || hasApartments"
        class="mt-10 mb-12"
      >
        <div v-if="hasBusinesses">
          <div class="d-flex align-center justify-space-between mb-3">
            <h2 class="text-h5 mb-0 font-weight-bold">{{ $t('My Businesses') }}</h2>
            <router-link
              :to="{ name: 'Businesses' }"
              class="font-weight-medium"
            >
              {{ $t('View all') }}
            </router-link>
          </div>
          <v-card outlined class="pa-3 rounded-lg">
            <places-table :limit="3" />
          </v-card>
        </div>

        <div
          v-if="hasApartments"
          :class="{ 'mt-12': hasBusinesses }"
        >
          <div class="d-flex align-center justify-space-between mb-3">
            <h2 class="text-h5 mb-0 font-weight-bold">{{ $t('My Apartments') }}</h2>
            <router-link
              :to="{ name: 'Ads', params: { categoryConst: 'apartment' } }"
              class="font-weight-medium"
            >
              {{ $t('View all') }}
            </router-link>
          </div>
          <v-card outlined class="pa-3 rounded-lg">
            <ads-table category-const="apartment" :limit="3" />
          </v-card>
        </div>
      </div>

      <v-row class="mt-12 mb-12">
        <v-col
          md="6"
          sm="6"
        >
          <v-card
            outlined
            class="px-6 pb-6 rounded-lg"
            height="100%"
          >
            <div class="d-flex flex-column mb-4">
              <h2 class="text-center my-4">{{ $t('For guests') }}</h2>
              <v-img
                class="rounded mx-auto"
                width="100%"
                max-height="250px"
                src="@/assets/dashboard-guests.png"
              ></v-img>
            </div>
            <div class="d-flex flex-column flex-lg-row">
              <v-row>
                <v-col>
                  <h3>
                    {{ $t('How to book accomodation') }}
                  </h3>
                  <p>
                    {{ $t('dashboard_how_to_book_accomodation') }}
                  </p>
                </v-col>
              </v-row>
            </div>
            <div class="mb-2">
              <h3>
                {{ $t('Terms of accomodation reservation') }}
              </h3>
              <p>
                {{ $t('Terms of reservation text') }}
              </p>
              <a target="_blank" :href="
                $i18n.locale === 'sr'
                  ? 'https://bookaweb.com/sr/uslovi-rezervacije-smestaja'
                  : 'https://bookaweb.com/accommodation-reservation-conditions'"
              >{{ $t('Terms of reservation') }}</a>
            </div>
            <div class="mt-6">
              <h4>
                {{ $t('We suggest some of the destinations') }}
              </h4>
            </div>
            <a
              v-for="(link, i) in links[$i18n.locale]"
              :key="`link_${i}`"
              class="mr-3"
              :href="link.url"
            >{{ link.text }}</a>
          </v-card>
        </v-col>

        <v-col
          md="6"
          sm="6"
        >
          <v-card
            outlined
            class="px-6 pb-6 rounded-lg"
          >
            <div class="d-flex flex-column mb-4">
              <h2 class="text-center my-4">{{ $t('For owners') }}</h2>
              <v-img
                class="rounded mx-auto"
                width="100%"
                max-height="250px"
                src="@/assets/dashboard-owners.jpeg"
              ></v-img>
            </div>
            <div class="d-flex flex-column flex-lg-row">
              <v-row>
                <v-col>
                  <h3>
                    {{ $t('How to list accomodation') }}
                  </h3>
                  <p>
                    {{ $t('dashboard_how_to_add_accomodation') }}
                  </p>
                </v-col>
              </v-row>
            </div>
            <div class="mt-4 mb-2">
              <div class="d-flex justify-left">
                <youtube-iframe url="https://www.youtube.com/embed/hZ3tfzxmB6w"></youtube-iframe>
              </div>
            </div>
            <div class="mt-5 mb-2">
              <div v-if="$i18n.locale === 'sr'" class="mb-5">
                <h3>
                  Help Centar za Vlasnike
                </h3>
                <p>
                  Detaljne informacije kako da iz ugla vlasnika koristite platformu i dodjete do većeg broja rezervacija.
                  <br/>
                  <a target="_blank" href="https://bookaweb.com/sr/help-centar-za-vlasnike">Help Centar za Vlasnike</a>
                </p>
              </div>
              <h3>
                {{ $t('Terms of renting accomodations') }}
              </h3>
              <p>
                {{ $t('Terms of renting text') }}
                <br/>
                <a target="_blank" :href="
                  $i18n.locale === 'sr'
                    ? 'https://bookaweb.com/sr/uslovi-izdavanja-smestaja'
                    : 'https://bookaweb.com/conditions-for-renting-an-accommodation'"
                >{{ $t('Terms of renting') }}</a>
              </p>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <app-promotion-banner/>

      <inqury-preview-modal @reload="loadPendingOwnerInquiries"/>

      <v-row class="mt-12">
        <v-col>
          <v-card
            width="100%"
            class="pa-5 d-lg-flex flex-column mb-6 rounded-lg"
            outlined
          >
            <h3 class="mb-4">{{ $t("Let's grow together") }}</h3>
            <p>
              {{ $t('grow_together_text') }}
            </p>
            <div class="d-flex align-items-center">
              <a
                target="_blank"
                :href="
                $i18n.locale === 'sr'
                  ? 'https://docs.google.com/forms/d/e/1FAIpQLSeSY04w0QYFN0U-bhEgRw7epvwmCdrNty7wLfdG7R9VxVRAMg/viewform?usp=sf_link'
                  : 'https://docs.google.com/forms/d/e/1FAIpQLSduqOEFbhuUEWPyyYmt1b5d88DosTeqDxWHMn5oWs37jOrIpg/viewform?usp=sf_link'"
              >{{ $t('Form for hosts') }}</a>
              <a
                target="_blank"
                :href="
                $i18n.locale === 'sr'
                  ? 'https://docs.google.com/forms/d/e/1FAIpQLSd71f8Rvx-8KGDBGeALLFUJ_eGvEKhmxLBUVT_VuZOpzZyNyQ/viewform?usp=sf_link'
                  : 'https://docs.google.com/forms/d/e/1FAIpQLSdskGozU9h2mq-frXnF2Lzs9FGFNAuiHmIH_gamLxuc_-RlLA/viewform?usp=sf_link'"
                class="ml-4"
              >{{ $t('Form for guests') }}</a>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </main-layout>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import YoutubeIframe from '@/components/YoutubeIframe.vue';
import AppPromotionBanner from '@/components/AppPromotionBanner.vue';
import PlacesTable from '@/components/tables/PlacesTable.vue';
import AdsTable from '@/components/tables/AdsTable.vue';
import InquriesInTable from '@/components/inquries/InquriesInTable.vue';
import InquryPreviewModal from '@/components/inquries/InquryPreviewModal.vue';

export default {
  components: {
    YoutubeIframe,
    AppPromotionBanner,
    PlacesTable,
    AdsTable,
    InquriesInTable,
    InquryPreviewModal,
  },
  computed: {
    ...mapGetters(['user']),
    hasBusinesses() {
      return Boolean(this.user.businessesCount);
    },
    hasApartments() {
      return Boolean(this.user.apartmentCount);
    },
  },
  mounted() {
    if (this.hasApartments) {
      this.loadPendingOwnerInquiries();
    }
  },
  data() {
    return {
      pendingOwnerInquiries: [],
      links: {
        sr: [
          {
            text: 'Beograd',
            url: `${process.env.VUE_APP_APP_URL}/sr/beograd/smestaj`,
          },
          {
            text: 'Zlatibor',
            url: `${process.env.VUE_APP_APP_URL}/sr/zlatibor/smestaj`,
          },
          {
            text: 'Novi Sad',
            url: `${process.env.VUE_APP_APP_URL}/sr/novi-sad/smestaj`,
          },
          {
            text: 'Niš',
            url: `${process.env.VUE_APP_APP_URL}/sr/nis/smestaj`,
          },
        ],
        en: [
          {
            text: 'Belgrade',
            url: `${process.env.VUE_APP_APP_URL}/belgrade/accommodation`,
          },
          {
            text: 'Zlatibor',
            url: `${process.env.VUE_APP_APP_URL}/zlatibor/accommodation`,
          },
          {
            text: 'Novi Sad',
            url: `${process.env.VUE_APP_APP_URL}/novi-sad/accommodation`,
          },
          {
            text: 'Niš',
            url: `${process.env.VUE_APP_APP_URL}/nis/accommodation`,
          },
        ],
      },
    };
  },
  methods: {
    loadPendingOwnerInquiries() {
      axios.get('/calendar/inquries/me', {
        params: {
          status: 'owner_pending',
          limit: 3,
        },
      })
        .then(({ data }) => {
          this.pendingOwnerInquiries = data.data || [];
        });
    },
  },
};
</script>
